import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAlmacen } from "@/lib/almacen";
import { useAuth } from "@/auth/AuthProvider";
import { useRpc } from "@/lib/useRpc";
import { num, fechaCorta } from "@/lib/formato";
import { Tarjeta, Boton, Campo, Textarea, Badge, Esqueleto } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import { detectarComaDecimal, parsearNumero } from "@/features/importar/parsear";

/**
 * Parsea "SKU, cantidad" por línea. Separador por prioridad: tab, punto y coma, la PRIMERA
 * coma (así "A, 12,5" es A → 12,5), o espacios. Suma repetidos. Ignora la cabecera pegada.
 */
export function parsearVentas(texto: string): { filas: { sku: string; cantidad: number }[]; invalidas: string[] } {
  const acumulado = new Map<string, number>();
  const invalidas: string[] = [];
  const partidas: { linea: string; sku: string; cantTexto: string }[] = [];
  for (const cruda of texto.split(/\r?\n/)) {
    const linea = cruda.trim();
    if (!linea) continue;
    if (/^(sku|clave|codigo|código|producto)\b/i.test(linea)) continue; // cabecera pegada
    let sku: string; let cantTexto: string;
    if (linea.includes("\t")) { const [a, ...r] = linea.split("\t"); sku = a; cantTexto = r.join(" "); }
    else if (linea.includes(";")) { const [a, ...r] = linea.split(";"); sku = a; cantTexto = r.join(" "); }
    else if (linea.includes(",")) { const i = linea.indexOf(","); sku = linea.slice(0, i); cantTexto = linea.slice(i + 1); }
    else { const m = linea.match(/^(\S+)\s+(.+)$/); if (!m) { invalidas.push(linea); continue; } sku = m[1]; cantTexto = m[2]; }
    partidas.push({ linea, sku: sku.trim().toUpperCase(), cantTexto: cantTexto.trim() });
  }
  // Misma regla que el importador: la coma es decimal solo si NINGÚN valor de la columna trae punto.
  const comaDecimal = detectarComaDecimal(partidas.map((p) => p.cantTexto));
  for (const p of partidas) {
    const n = parsearNumero(p.cantTexto, comaDecimal);
    if (!p.sku || n === null || n === "invalido" || n <= 0) { invalidas.push(p.linea); continue; }
    acumulado.set(p.sku, (acumulado.get(p.sku) ?? 0) + n);
  }
  return { filas: [...acumulado].map(([sku, cantidad]) => ({ sku, cantidad })), invalidas };
}

export function VentasDia() {
  const { almacen } = useAlmacen();
  const { perfil } = useAuth();
  const toast = useToast();
  const [texto, setTexto] = useState("");
  const [preview, setPreview] = useState(false);
  const [resultado, setResultado] = useState<{ ok: number; rechazadas: { sku: string; motivo: string; disponible?: number; solicitado?: number }[] } | null>(null);
  const parseo = useMemo(() => parsearVentas(texto), [texto]);

  const lotesHoy = useQuery({
    queryKey: ["hoy", "lotes", almacen?.id],
    enabled: !!almacen,
    queryFn: async () => {
      const { data, error } = await supabase.from("v_hoy_movimientos").select("lote_id, fecha").eq("almacen_id", almacen!.id).eq("motivo", "venta").not("lote_id", "is", null);
      if (error) throw error;
      const porLote = new Map<string, { fecha: string; n: number }>();
      for (const r of data) { const l = porLote.get(r.lote_id!) ?? { fecha: r.fecha!, n: 0 }; l.n += 1; porLote.set(r.lote_id!, l); }
      return [...porLote.values()];
    },
  });

  const aplicar = useRpc("registrar_ventas_lote", {
    onSuccess: (data) => {
      const r = Array.isArray(data) ? data[0] : (data as never);
      setResultado({ ok: r.ok, rechazadas: (r.rechazadas as never) ?? [] });
      setPreview(false);
      setTexto("");
      if (r.ok === 0) toast.error("Ninguna venta se pudo aplicar; revisa las rechazadas."); else toast.exito(`${r.ok} venta(s) registradas`);
    },
  });

  if (perfil?.empresa.modo_ventas !== "manual") {
    return <div className="mx-auto max-w-2xl"><Tarjeta><p>Tus ventas las registra tu sistema de punto de venta. Esta pantalla solo aplica a empresas que llevan las ventas a mano.</p></Tarjeta></div>;
  }
  if (!almacen) return <Esqueleto />;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div><h1 className="text-2xl font-bold">Ventas del día</h1><p className="text-sm text-tinta-suave">Pega lo que vendiste hoy (una línea por producto: clave y cantidad). Se descuenta del físico de {almacen.nombre}.</p></div>

      {lotesHoy.data && lotesHoy.data.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-alerta-suave px-4 py-3 text-sm text-amber-900">
          Hoy ya aplicaste {lotesHoy.data.length} lote(s): {lotesHoy.data.map((l) => `${fechaCorta(l.fecha)} (${l.n} productos)`).join(", ")}. Si pegas lo mismo otra vez, se descuenta doble.
        </div>
      )}

      {resultado && (
        <Tarjeta titulo="Resultado">
          <p className="mb-2"><strong>{resultado.ok}</strong> productos descontados.</p>
          {resultado.rechazadas.length > 0 && (
            <>
              <p className="mb-1 text-sm font-medium text-red-800">{resultado.rechazadas.length} rechazados:</p>
              <ul className="divide-y divide-borde text-sm">
                {resultado.rechazadas.map((r, i) => (
                  <li key={i} className="flex justify-between py-1.5">
                    <span className="font-medium">{r.sku ?? "(sin clave)"}</span>
                    <span className="text-tinta-suave">{r.motivo === "STOCK_INSUFICIENTE" ? `solo hay ${num(r.disponible)}, pediste ${num(r.solicitado)} → cuéntalo primero` : r.motivo === "SKU_DESCONOCIDO" ? "no está en el catálogo" : r.motivo}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <Boton variante="secundario" tamano="sm" className="mt-3" onClick={() => setResultado(null)}>Registrar otro lote</Boton>
        </Tarjeta>
      )}

      {!resultado && (
        <Tarjeta>
          {!preview ? (
            <div className="space-y-3">
              <Campo etiqueta="Ventas (clave, cantidad)" htmlFor="ventas" ayuda="Puedes pegar dos columnas desde Excel. Los repetidos se suman.">
                <Textarea id="ventas" value={texto} onChange={(e) => setTexto(e.target.value)} className="tabular min-h-48 font-mono text-sm" placeholder={"CAB-1234, 3\nTUB-2201\t5\nILU-6601, 12"} />
              </Campo>
              <div className="flex flex-wrap items-center gap-3">
                <Boton onClick={() => setPreview(true)} disabled={parseo.filas.length === 0}>Revisar {parseo.filas.length > 0 ? `${parseo.filas.length} productos` : ""}</Boton>
                {parseo.invalidas.length > 0 && <Badge tono="alerta">{parseo.invalidas.length} línea(s) que no entendí</Badge>}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <h2 className="font-semibold">Vista previa</h2>
              <p className="text-sm text-tinta-suave">{parseo.filas.length} productos · {num(parseo.filas.reduce((s, f) => s + f.cantidad, 0))} unidades en total</p>
              <ul className="tabular max-h-64 divide-y divide-borde overflow-auto rounded border border-borde text-sm">
                {parseo.filas.map((f) => <li key={f.sku} className="flex justify-between px-3 py-1.5"><span>{f.sku}</span><span>{num(f.cantidad)}</span></li>)}
              </ul>
              {parseo.invalidas.length > 0 && <p className="text-sm text-amber-900">Se ignorarán: {parseo.invalidas.slice(0, 5).join(" · ")}{parseo.invalidas.length > 5 ? "…" : ""}</p>}
              <div className="flex gap-2">
                <Boton onClick={() => aplicar.mutate({ p_almacen_id: almacen.id, p_filas: parseo.filas as never })} cargando={aplicar.isPending}>Aplicar ventas</Boton>
                <Boton variante="secundario" onClick={() => setPreview(false)}>Volver a editar</Boton>
              </div>
            </div>
          )}
        </Tarjeta>
      )}
    </div>
  );
}
